import React, { useRef, useEffect, useState } from 'react';
import { ContextMenuProps, MentionItem } from './types';
import { filterItems, getThemeClasses } from './utils';
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation';

export const ContextMenu: React.FC<ContextMenuProps> = ({
  isOpen,
  position,
  items,
  selectedIndex,
  searchQuery,
  onSelect,
  onClose,
  onSearch,
  onNavigateBack,
  canNavigateBack = false,
  maxHeight = 300,
  theme = 'auto'
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  const filteredItems = filterItems(items, localSearchQuery);
  const themeClasses = getThemeClasses(theme);

  useKeyboardNavigation({
    isMenuOpen: isOpen,
    items: filteredItems,
    selectedIndex,
    onSelect,
    onClose,
    onNavigateBack,
    canNavigateBack,
    setSelectedIndex: (index) => {
      // This will be handled by the parent component
    }
  });

  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearchQuery(value);
    onSearch(value);
  };

  const handleItemClick = (item: MentionItem) => {
    onSelect(item);
  };

  const renderItem = (item: MentionItem, index: number) => {
    const isSelected = index === selectedIndex;
    const itemClasses = `${themeClasses.item} ${
      isSelected ? themeClasses.itemSelected : themeClasses.itemHover
    }`;

    return (
      <div
        key={item.id}
        className={itemClasses}
        onClick={() => handleItemClick(item)}
        onMouseEnter={() => {
          // Update selected index on hover
        }}
      >
        <div className="flex items-center gap-3">
          {item.icon && (
            <span className="text-lg flex-shrink-0">{item.icon}</span>
          )}
          {item.avatar && (
            <img
              src={item.avatar}
              alt={item.label}
              className="w-6 h-6 rounded-full flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{item.label}</div>
            {item.description && (
              <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                {item.description}
              </div>
            )}
          </div>
          {item.children && (
            <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">
              →
            </span>
          )}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className={`fixed z-50 min-w-64 max-w-80 ${themeClasses.menu}`}
      style={{
        left: position.x,
        top: position.y,
        maxHeight: `${maxHeight}px`
      }}
    >
      {/* Header with search and back button */}
      <div className="flex items-center">
        {canNavigateBack && (
          <button
            onClick={onNavigateBack}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Go back"
          >
            <span className="text-gray-500 dark:text-gray-400">←</span>
          </button>
        )}
        <input
          ref={searchInputRef}
          type="text"
          value={localSearchQuery}
          onChange={handleSearchChange}
          placeholder="Search..."
          className={`${themeClasses.search} ${canNavigateBack ? 'pl-2' : ''}`}
        />
      </div>

      {/* Items list */}
      <div className="max-h-64 overflow-y-auto">
        {filteredItems.length > 0 ? (
          filteredItems.map((item, index) => renderItem(item, index))
        ) : (
          <div className="px-3 py-4 text-center text-gray-500 dark:text-gray-400">
            No items found
          </div>
        )}
      </div>
    </div>
  );
};
